// Shared generation pipeline: turns a CreateRequest into an in-memory RoastArtifact
// plus the raw inputs a later save needs. Extracted from StudioCreate's
// handleGeneratePhoto / handleGenerateDeck so Processing and StudioCreate share one
// source of truth. Photo path here; deck/doc path added in Task 5.
import type { CreateRequest } from "./createFlow";
import { generateRoast, synthesizeSpeech, generateDoodle, comprehendDeck, voiceDeck, detectDeckRegions } from "@/lib/functions";
import { uploadSourcePhoto, uploadSourcePdf } from "@/lib/storage";
import { renderPages as renderPagesPdf } from "@/lib/pdf";
import { resolveAudioAssets } from "@/player/audio/director";
import type { RoastArtifact, DetectedObject, WordTiming, KeySlide } from "@/types/roast";
import { runDeckPipeline, type DeckRaster, type DeckStageKey } from "@/lib/deckPipeline";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE } from "@/config/censorship";
import { swapSoundalikes, findUnhandledProfanity } from "@/player/profanity";
import { sanitizeWithLog } from "./sanitizeScript";
import { base64ToObjectUrl } from "./blobUrl";

export type StageId = "upload" | "script" | "comprehend" | "render" | "voice" | "assemble" | "done";
export interface ProgressEvent { stage: StageId; done?: number; total?: number; text?: string; }
export type SaveInputs =
  | { source: "photo"; imageFile: File; audioBase64: string; doodleBase64?: string; doodleMimeType?: string }
  | { source: "deck"; audioBase64: string;
      keyRasters: DeckRaster[]; fillerRasters: DeckRaster[]; keySlides: KeySlide[] };
export interface GenerationResult { artifact: RoastArtifact; saveInputs: SaveInputs; }
export interface GenerationDeps {
  user: { uid: string; email: string | null };
  onStage: (e: ProgressEvent) => void;
  signal?: AbortSignal;
}

/** Read a File as a base64 string (without the data-URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:<mime>;base64,<data>" — strip the prefix
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");
}

export async function runGeneration(req: CreateRequest, deps: GenerationDeps): Promise<GenerationResult> {
  if (req.detectedType === "photo") return runPhoto(req, deps);
  return runDeck(req, deps);
}

/** Deck pipeline stage keys → the shared StageId surfaced to Processing. */
const DECK_KEY_TO_STAGE: Record<DeckStageKey, StageId> = {
  upload: "upload", comprehend: "comprehend", render: "render",
  voice: "voice", schedule: "assemble", done: "done",
};

// Shared for deck AND doc — both ride the deck/rendering engine; only the aspect /
// sourceType stamp differs. Mirrors StudioCreate.handleGenerateDeck.
async function runDeck(req: CreateRequest, deps: GenerationDeps): Promise<GenerationResult> {
  const { user, onStage, signal } = deps;
  const file = req.source;
  const id = req.roastId;
  const isDoc = req.detectedType === "doc";

  const result = await runDeckPipeline(
    { file, uid: user.uid, roastId: id, mode: req.mode, profanity: req.profanity,
      name: req.name, context: req.context, voiceId: req.voiceId, pageCount: req.pageCount,
      sourceType: isDoc ? "doc" : "deck", doodleKeySlides: req.doodle, bleepMode: req.bleepMode },
    {
      uploadPdf: uploadSourcePdf,
      comprehendDeck: (r) => comprehendDeck(r),
      // Forward each rendered page to deckPipeline's onPage (ticks the cumulative counter);
      // do NOT emit per-pass render events here — that reset "N of M" backward at the tier boundary.
      renderPages: (f, indices, scale, onPage) =>
        renderPagesPdf(f, indices, scale, { onPage: onPage ? (_p, d, t) => onPage(d, t) : undefined }),
      voiceDeck: (r) => voiceDeck(r),
      detectDeckRegions: (r) => detectDeckRegions(r),
      resolveAudio: resolveAudioAssets,
      onStage: (key) => onStage({ stage: DECK_KEY_TO_STAGE[key] }),
      onScript: (text) => onStage({ stage: "comprehend", text }),
      // Cumulative + monotonic render progress → the shared "render" stage event.
      onRenderProgress: (done, total) => onStage({ stage: "render", done, total }),
      doodleSlide: (r) => generateDoodle(r),
    },
  );
  throwIfAborted(signal);

  const audioUrl = base64ToObjectUrl(result.audioBase64, result.mimeType);
  const now = Date.now();
  const assembled: RoastArtifact = {
    id, ownerUid: user.uid, ownerEmail: user.email ?? "",
    createdAt: now, updatedAt: now, status: "ready", visibility: "private",
    mode: req.mode, profanity: req.profanity, title: result.title, script: result.script, scriptOriginal: result.script,
    name: req.name, context: req.context,
    imagePath: null, imageUrl: null, audioPath: null, audioUrl,
    musicTrack: result.audio.musicTrack, durationSec: result.durationSec,
    wordTimings: result.wordTimings, detectedObjects: [], censorRegions: [],
    voiceName: req.voiceId, isCharacterFullscreen: false, aspect: isDoc ? "9:16" : "16:9", viewCount: 0,
    audio: result.audio,
    captionMask: req.captionMask ?? DEFAULT_CAPTION_MASK,
    bleepMode: req.bleepMode ?? DEFAULT_BLEEP_MODE,
    sourceType: isDoc ? "doc" : "deck", deckPdfPath: result.pdfPath,
    keySlides: result.keySlides, fillerImages: result.fillerImages,
    slideSchedule: result.slideSchedule, verdict: result.verdict,
  };

  return {
    artifact: assembled,
    saveInputs: { source: "deck", audioBase64: result.audioBase64,
      keyRasters: result.keyRasters, fillerRasters: result.fillerRasters, keySlides: result.keySlides },
  };
}

async function runPhoto(req: CreateRequest, deps: GenerationDeps): Promise<GenerationResult> {
  const { user, onStage, signal } = deps;
  const file = req.source;
  const rid = req.roastId;
  const doodleEnabled = req.doodle;

  // 1) upload source photo (Storage rules-validated client write)
  onStage({ stage: "upload" });
  const { imagePath } = await uploadSourcePhoto(user.uid, rid, file);
  throwIfAborted(signal);

  // 2) generate script + title + detected objects
  onStage({ stage: "script" });
  const gen = await generateRoast({
    mode: req.mode,
    profanity: req.profanity,
    name: req.name,
    context: req.context,
    length: req.length,
    imagePath,
  });
  throwIfAborted(signal);
  // Pre-TTS text prep on the ONE source string (audio == display == alignment-input):
  // 1. sanitizeScript — deterministic backstop for model self-check leaks ("[1] … profanity
  //    counts …"; documented in functions/src/generateRoast.ts) + stage-direction residue.
  // 2. soundalike swap — cusses → clean minced-oaths when bleepMode==="soundalike".
  // Title + object references get the same prep so nothing leaks anywhere it's spelled.
  const soundalike = (req.bleepMode ?? DEFAULT_BLEEP_MODE) === "soundalike";
  const prep = (s: string, label: string): string => {
    const clean = sanitizeWithLog(s, label);
    return soundalike ? swapSoundalikes(clean) : clean;
  };
  const script = prep(gen.data.script, "photo script");
  // Breadcrumb: real curse stems that survived the swap (no clean stand-in exists) — they
  // still get bleeped via stem detection, but surface them so the tables grow from evidence.
  if (soundalike) {
    const unhandled = findUnhandledProfanity(script);
    if (unhandled.length) console.warn("[censor] spoken-but-bleeped (no clean stand-in):", unhandled);
  }
  // Surface the generated script text on the progress channel (feeds the redacted
  // writing-stream flourish) before kicking off voice synthesis.
  onStage({ stage: "script", text: script });
  const title = prep(gen.data.title, "photo title");
  const detectedObjects: DetectedObject[] = (gen.data.detectedObjects ?? []).map((o) => ({
    ...o, dialogue_reference: prep(o.dialogue_reference, "photo dialogue_reference"),
  }));
  const audioProfile = gen.data.audioProfile;

  // 3) synthesize speech (Gemini-TTS) + word timings, concurrently with the
  // optional doodle — both depend only on `script` (the doodle also on the
  // original `file`), and neither uses the other's output. Kick both off,
  // then await each.
  onStage({ stage: "voice" });
  const ttsPromise = synthesizeSpeech({ text: script, mode: req.mode, voiceId: req.voiceId, profanity: req.profanity });
  const doodlePromise = doodleEnabled
    ? fileToBase64(file).then((photoBase64) =>
        generateDoodle({
          image: photoBase64,
          mimeType: file.type || "image/jpeg",
          mode: req.mode,
          script,
        }),
      )
    : null;

  const tts = await ttsPromise;
  throwIfAborted(signal);
  const audioBase64 = tts.data.audioBase64; // retain raw bytes for save
  const audioUrl = base64ToObjectUrl(tts.data.audioBase64, tts.data.mimeType);
  const wordTimings: WordTiming[] = tts.data.wordTimings ?? [];
  const durationSec = wordTimings.length
    ? wordTimings[wordTimings.length - 1].end
    : 0;

  // Resolve audio assets (music track + SFX cues) from the AI-generated profile.
  const audio = resolveAudioAssets(audioProfile, script, wordTimings, req.mode, rid);

  // 4) optional doodle — only when the toggle is on
  let doodleBase64: string | undefined;
  let doodleMimeType: string | undefined;
  if (doodlePromise) {
    const doodleRes = await doodlePromise;
    throwIfAborted(signal);
    doodleBase64 = doodleRes.data.imageBase64; // retain raw bytes for save
    doodleMimeType = doodleRes.data.mimeType; // retain mime for the preview object URL
  }

  // 5) assemble in-memory RoastArtifact for live preview
  // Use a same-origin object URL (from the picked File) for the live preview + video
  // capture. The Storage download URL is cross-origin and would taint the capture
  // canvas → VideoFrame construction fails (deck slides work because they're blobs).
  // imagePath still feeds generateRoast, and saveRoast re-uploads `file` for the
  // persisted imageUrl, so this only changes the in-session preview/capture source.
  onStage({ stage: "assemble" });
  const localImageUrl = URL.createObjectURL(file);
  const now = Date.now();
  const assembled: RoastArtifact = {
    id: rid,
    ownerUid: user.uid,
    ownerEmail: user.email ?? "",
    createdAt: now,
    updatedAt: now,
    status: "ready",
    visibility: "private",
    mode: req.mode,
    profanity: req.profanity,
    title,
    script,
    scriptOriginal: script,
    name: req.name,
    context: req.context,
    imagePath,
    imageUrl: localImageUrl,
    audioPath: null,
    audioUrl,
    musicTrack: audio.musicTrack,
    durationSec,
    wordTimings,
    detectedObjects,
    censorRegions: [],
    voiceName: req.voiceId,
    isCharacterFullscreen: !localImageUrl,
    aspect: req.aspect,
    viewCount: 0,
    audio,
    captionMask: req.captionMask ?? DEFAULT_CAPTION_MASK,
    bleepMode: req.bleepMode ?? DEFAULT_BLEEP_MODE,
  };

  onStage({ stage: "done" });
  return {
    artifact: assembled,
    saveInputs: { source: "photo", imageFile: file, audioBase64, ...(doodleBase64 ? { doodleBase64, doodleMimeType } : {}) },
  };
}
